import type { AgentDiffView, PromptDiffView } from '@ai-sales/shared'
import { cn } from '../lib/cn.js'

/**
 * Показ разницы двух версий.
 *
 * Два правила, которые здесь важнее оформления.
 *
 * **Цвет не единственный носитель смысла.** У каждой строки слева стоит
 * знак `+` или `−`, и его видно независимо от того, различает ли человек
 * зелёный и красный. Тот же принцип, что у меток состояния.
 *
 * **Номера строк — с обеих сторон.** Слева номер в прежней версии, справа
 * в новой. Одна колонка номеров выглядит опрятнее и врёт: после первого
 * же добавления она перестаёт совпадать с одним из текстов, и найти
 * строку в редакторе по ней нельзя.
 */
export function TextDiffView({ diff }: { diff: PromptDiffView }) {
  if (diff.summary.identical) {
    return (
      <p className="rounded-md border border-line bg-raised px-4 py-6 text-center text-sm text-muted">
        Версии {diff.from.version} и {diff.to.version} совпадают дословно.
      </p>
    )
  }

  return (
    <div className="overflow-x-auto rounded-md border border-line bg-raised">
      <table className="w-full border-collapse font-mono text-xs">
        <caption className="sr-only">
          Разница между версией {diff.from.version} и версией {diff.to.version}
        </caption>
        <tbody>
          {diff.lines.map((line, index) => (
            <tr
              key={`${line.kind}-${index}`}
              className={cn(
                line.kind === 'added' && 'bg-accent-soft',
                line.kind === 'removed' && 'bg-crimson-soft',
              )}
            >
              <td className="w-10 border-r border-line-soft px-2 py-0.5 text-right align-top text-muted select-none">
                {line.fromLine ?? ''}
              </td>
              <td className="w-10 border-r border-line-soft px-2 py-0.5 text-right align-top text-muted select-none">
                {line.toLine ?? ''}
              </td>
              <td
                className={cn(
                  'w-6 px-1 py-0.5 text-center align-top select-none',
                  line.kind === 'added' && 'text-accent',
                  line.kind === 'removed' && 'text-crimson',
                  line.kind === 'equal' && 'text-muted',
                )}
              >
                {line.kind === 'added' ? '+' : line.kind === 'removed' ? '−' : ''}
              </td>
              <td className="px-2 py-0.5 whitespace-pre-wrap text-ink">{line.text || ' '}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export function DiffSummaryLine({ diff }: { diff: PromptDiffView }) {
  if (diff.summary.identical) return <span className="text-muted">без изменений</span>

  return (
    <span className="text-muted">
      <span className="text-accent">+{diff.summary.added}</span>{' '}
      <span className="text-crimson">−{diff.summary.removed}</span> строк
    </span>
  )
}

/**
 * Разница конфигураций агента.
 *
 * Привязки промптов идут первыми и отдельной таблицей. Это не оформление:
 * смена версии системной инструкции меняет поведение сильнее любого поля
 * конфигурации, и теряться в одном ряду с «температура 0,7 → 0,6» она
 * не должна.
 */
export function AgentDiffTable({ diff }: { diff: AgentDiffView }) {
  if (diff.identical) {
    return (
      <p className="rounded-md border border-line bg-raised px-4 py-6 text-center text-sm text-muted">
        Версии {diff.from.version} и {diff.to.version} настроены одинаково.
      </p>
    )
  }

  return (
    <div className="flex flex-col gap-4">
      {diff.promptChanges.length > 0 && (
        <ChangeTable
          caption="Промпты"
          rows={diff.promptChanges.map((change) => ({
            label: PROMPT_ROLE_TITLES[change.role] ?? change.role,
            before: change.before ?? 'не задан',
            after: change.after ?? 'не задан',
          }))}
          highlight
        />
      )}

      {diff.changes.length > 0 && (
        <ChangeTable
          caption="Настройки"
          rows={diff.changes.map((change) => ({
            label: change.label,
            before: change.before,
            after: change.after,
          }))}
        />
      )}
    </div>
  )
}

const PROMPT_ROLE_TITLES: Record<string, string> = {
  system: 'Системная инструкция',
  validation: 'Проверка ответа',
  followup_step: 'Шаг follow-up',
  followup_last: 'Последний шаг follow-up',
}

function ChangeTable({
  caption,
  rows,
  highlight = false,
}: {
  caption: string
  rows: { label: string; before: string; after: string }[]
  highlight?: boolean
}) {
  return (
    <div
      className={cn(
        'overflow-x-auto rounded-md border bg-raised',
        highlight ? 'border-copper/40' : 'border-line',
      )}
    >
      <table className="w-full border-collapse text-sm">
        <caption className="border-b border-line-soft px-3 py-2 text-left text-xs font-semibold tracking-wide text-muted uppercase">
          {caption}
        </caption>
        <thead>
          <tr className="text-xs text-muted">
            <th className="px-3 py-1.5 text-left font-medium">Поле</th>
            <th className="px-3 py-1.5 text-left font-medium">Было</th>
            <th className="px-3 py-1.5 text-left font-medium">Стало</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.label} className="border-t border-line-soft align-top">
              <td className="px-3 py-1.5 text-ink-soft">{row.label}</td>
              <td className="px-3 py-1.5 font-mono text-xs break-all text-crimson">{row.before}</td>
              <td className="px-3 py-1.5 font-mono text-xs break-all text-accent">{row.after}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
